import React, { useState, useEffect, useRef, useContext, FC } from "react";
import { Redirect } from "react-router";
import TranslationsContext from "../../infrastructure/context/translations/translations-context";
import {
  TranslationKeys,
  RouterPaths,
  CustomPropertyKeys,
} from "../../infrastructure/const";
import { GetHttpClientInstance } from "../../infrastructure/context/http/http-context-provider";
import { UpdateCartPropertiesRequest } from "../../infrastructure/http/modules/cart/cart.models";
import PaymentIcons from "../common/payment-icons";
import StickyBanner from "./cart-summary/sticky-banner";
import OrderSummary from "./cart-summary/order-summary";
import PriceSection from "./cart-summary/price-section";
import ShippingPriceDisclaimer from "./cart-summary/shipping-price-disclaimer";
import UserContext from "../../infrastructure/context/user/user.context";
import useVatExempt from "./cart-summary/useVatExempt";
import CheckUtils from "../../utilities/check-utils";
import ConfigurationContext from "../../infrastructure/context/configuration/configuration.context";
import TranslationManager from "../../infrastructure/translation-manager";

type Props = {
  updateMessage: (message: string) => void;
};

const CartSummary: FC<Props> = ({ updateMessage }) => {
  const userContext = useContext(UserContext);
  const { getFeatureFlagValue, minOrderAmount } = useContext(ConfigurationContext);
  const translationsContext = useContext(TranslationsContext);
  const translationManager = new TranslationManager(translationsContext);
  const { cart } = userContext;

  const hidePricesAndPayment = getFeatureFlagValue("hidePricesAndPayment");

  const { isApplied } = useVatExempt();

  const hasCustomProperties = !CheckUtils.isNullObject(cart.customProperties);
  const cartTableRef = useRef<HTMLTableElement>(null);

  const [navigateToCheckout, setNavigateToCheckout] = useState(false);

  const [properties, setProperties] = useState(
    hasCustomProperties ? userContext.cart.customProperties : {}
  );
  const [vatExemptApplied, setVatExemptApplied] = useState(
    hasCustomProperties && isApplied(userContext.cart.customProperties)
  );
  const [isStickyBannerVisible, setIsStickyBannerVisible] = useState(true);
  const [couponResponseCode, setCouponResponseCode] = useState(
    userContext.cart.couponResponseCode
  );

  const minOrderAmountNotAchieved = minOrderAmount != null && minOrderAmount > userContext.cart.priceBreakdown.totalPrice;
  const missingAmount = minOrderAmount? minOrderAmount - userContext.cart.priceBreakdown.totalPrice : 0;


  useEffect(() => {
    const handleStickyBannerDisplay = () => {
      if (cartTableRef.current) {
        const isElementVisible =
          cartTableRef.current.getBoundingClientRect().bottom <=
          window.innerHeight;
        setIsStickyBannerVisible(!isElementVisible);
      }
    };

    document.addEventListener("scroll", handleStickyBannerDisplay);
    return () =>
      document.removeEventListener("scroll", handleStickyBannerDisplay);
  }, []);

  const updateProperties = (
    key: string,
    value: string
  ): Promise<{
    [x: string]: string;
  }> => {
    return new Promise((resolve) => {
      setProperties((prevProperties) => {
        const updatedProperties = { ...prevProperties, [key]: value };
        resolve(updatedProperties);
        return updatedProperties;
      });
    });
  };

  const updateCart = (updatedProperties?: {
    [x: string]: string;
  }): Promise<void> => {
    return new Promise((resolve) => {
      GetHttpClientInstance()
        .cart.updateProperties({
          userId: userContext.user.userProfile.userId,
          customProperties: updatedProperties ? updatedProperties : properties,
          shippingMethod: userContext.cart.selectedShippingType,
          paymentCode: userContext.cart.selectedPaymentType,
        } as UpdateCartPropertiesRequest)
        .then(() => {
          userContext.refreshCart().then((response) => {
            setCouponResponseCode(response.couponResponseCode);
            resolve();
          });
        });
    });
  };

  const handlePropertyUpdate = (
    key: string,
    value: string | boolean,
    skipCartUpdate: boolean,
    successMessage?: string,
    cancelMessage?: string
  ) => {
    updateProperties(key, String(value)).then((updatedProperties) => {
      if (!skipCartUpdate) {
        updateCart(updatedProperties);
      }

      if (typeof value === "string" && successMessage && cancelMessage) {
        updateMessage(value.trim() === "" ? cancelMessage : successMessage);
      }

      if (
        key === CustomPropertyKeys.UK.VatExempt &&
        typeof value === "boolean"
      ) {
        setVatExemptApplied(value);
      }
    });
  };

  const handleGoToCheckout = () => {
    if(!minOrderAmountNotAchieved) {
      setNavigateToCheckout(true);
    }
  };

  return (
    <>
      {navigateToCheckout && <Redirect push to={RouterPaths.CheckoutPage} />}
      <StickyBanner
        isVisible={isStickyBannerVisible}
        handleCheckoutClick={handleGoToCheckout}
      />
      <div className="cart-collateral">
        <div>
          <div className="title">
            {translationManager.getTranslation(
              TranslationKeys.CART_ORDERSUMMARY_HEADER
            )}
          </div>
          {translationManager.getTranslation(
            TranslationKeys.CART_PROMOCODE_REMINDER
          ) && (
            <div className="coupon-code-reminder">
              {translationManager.getTranslation(
                TranslationKeys.CART_PROMOCODE_REMINDER
              )}
            </div>
          )}
          <div className="cart-info">
            <OrderSummary
              properties={properties}
              handlePropertyUpdate={handlePropertyUpdate}
              couponResponseCode={couponResponseCode}
            />
            <PriceSection
              cartTableRef={cartTableRef}
              vatExemptApplied={vatExemptApplied}
            />
            <ShippingPriceDisclaimer />
            {minOrderAmountNotAchieved && <>
              <div className="min-order-amount-container">
                <p className="min-order-amount-msg">
                  {translationManager.getTranslation(
                    TranslationKeys.MIN_ORDER_AMOUNT_NOT_ACHIEVED
                  ).replace('{0}', missingAmount.toFixed(2))}
                </p>
              </div>              
            </>}
            <button
              type="button"
              className="button-addToCart buy-button"
              title="Go to checkout"
              disabled={minOrderAmountNotAchieved}
              onClick={handleGoToCheckout}
            >
              {translationManager.getTranslation(
                TranslationKeys.CART_GOTOCHECKOUT
              )}
            </button>
            <div className="link-wrapper">
              <a
                href={RouterPaths.LandingPage}
                className="continue-link"
                title="Continue Shopping"
              >
                {translationManager.getTranslation(
                  TranslationKeys.CART_CONTINUESHOPPING
                )}
              </a>
            </div>
          </div>
        </div>
        {!hidePricesAndPayment && (
          <PaymentIcons
            paymentMethodsTranslation={translationManager.getTranslation(
              TranslationKeys.ACCEPTEDPAYMENTMETHODS
            )}
          />
        )}
      </div>
    </>
  );
};

export default CartSummary;
